import { BenefitType, clone, CompanySettings } from "@pentacode/core";
import type { Euros, Meals, Rate } from "@pentacode/openapi";
import { css, html, LitElement } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { alert, confirm } from "../../elements/alert-dialog";
import "../../elements/scroller";
import "../../elements/sortable-list";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import "./components/benefit-type-dialog";
import type { BenefitTypeDialog } from "./components/benefit-type-dialog";
import "./components/bonus-type-item";

@customElement("ptc-settings-benefits")
export class SettingsBenefits extends Routing(StateMixin(LitElement)) {
    routePattern = /settings\/benefits/;

    @state()
    private _loading = false;

    @state()
    private _benefitTypes: BenefitType[] = [];

    @singleton("ptc-benefit-type-dialog")
    private _benefitTypeDialog: BenefitTypeDialog;

    @query("#mealsSettingsForm")
    private _mealsSettingsForm: HTMLFormElement;

    private get _mealsSettingsData(): Pick<CompanySettings, `mealValue${"Breakfast" | "Lunch" | "Dinner"}`> | null {
        if (!this._mealsSettingsForm) {
            return null;
        }
        const data = new FormData(this._mealsSettingsForm);
        return {
            mealValueBreakfast: data.get("mealValueBreakfast")
                ? (Number(data.get("mealValueBreakfast") as string) as Rate<Euros, Meals>)
                : null,
            mealValueLunch: data.get("mealValueLunch")
                ? (Number(data.get("mealValueLunch") as string) as Rate<Euros, Meals>)
                : null,
            mealValueDinner: data.get("mealValueDinner")
                ? (Number(data.get("mealValueDinner") as string) as Rate<Euros, Meals>)
                : null,
        };
    }

    private get _hasMealsSettingsChanged() {
        if (!app.company || !this._mealsSettingsData) {
            return false;
        }
        for (const [prop, value] of Object.entries(this._mealsSettingsData) as [keyof CompanySettings, any][]) {
            if (app.company.settings[prop] !== value) {
                return true;
            }
        }
    }

    private _reset() {
        if (!app.company) {
            return;
        }
        this._benefitTypes = app.company.benefitTypes?.map(clone) || [];
        for (const [prop, value] of Object.entries(app.company.settings)) {
            const input = (this.renderRoot.querySelector(`[name="${prop}"]`) as HTMLInputElement) || null;
            if (input) {
                input.value = typeof value === "number" ? value.toFixed(2) : value;
            }
        }
        this.requestUpdate();
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("active") && this.active) {
            this._reset();
        }
    }

    private async _addBenefitType(config: BenefitType | undefined = new BenefitType()) {
        const submitted = await this._benefitTypeDialog.show(config);

        if (submitted) {
            this._benefitTypes.push(config);
            await this._submitBenefitTypes();
        }
    }

    private async _editBenefitType(config: BenefitType) {
        const updated = await this._benefitTypeDialog.show(config);
        if (updated) {
            Object.assign(config, updated);
            await this._submitBenefitTypes();
        }
    }

    private async _removeBenefitType(index: number) {
        if (
            await confirm("Sind Sie sicher dass Sie diese Sonderleistung löschen möchten?", "Löschen", "Abbrechen", {
                type: "destructive",
            })
        ) {
            this._benefitTypes.splice(index, 1);
            await this._submitBenefitTypes();
        }
    }

    private async _orderChanged() {
        this._benefitTypes.forEach((t, i) => (t.order = i));
        await this._submitBenefitTypes();
    }

    private async _submitBenefitTypes() {
        if (!app.company) {
            return;
        }
        this._loading = true;
        try {
            await app.updateCompany({ benefitTypes: this._benefitTypes });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._reset();
        this._loading = false;
    }

    private async _submitMealsSettings(e: Event) {
        e.preventDefault();
        if (!app.company) {
            return;
        }
        this._loading = true;
        try {
            await app.updateCompany({
                settings: new CompanySettings({
                    ...app.company.settings,
                    ...this._mealsSettingsData,
                }),
            });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._reset();
        this._loading = false;
    }

    static styles = [
        shared,
        css`
            .meals-row {
                display: grid;
                grid-template-columns: 1fr 7em;
                gap: 0.5em;
                align-items: center;
            }
        `,
    ];

    render() {
        return html`
            <ptc-scroller class="fullbleed">
                <div class="double-padded narrow-content">
                    <div class="padded">
                        <h2>Mitarbeiteressen</h2>

                        <form
                            id="mealsSettingsForm"
                            @input=${() => this.requestUpdate()}
                            @submit=${this._submitMealsSettings}
                        >
                            <div class="box">
                                <div class="half-padded meals-row border-bottom">
                                    <div class="padded stretch">
                                        <ptc-ui-icon name="icon.object.meal-breakfast"></ptc-ui-icon> Frühstück
                                    </div>
                                    <div class="right icon input">
                                        <input
                                            name="mealValueBreakfast"
                                            type="number"
                                            step="0.01"
                                            class="text-right-aligning"
                                        />
                                        <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>
                                    </div>
                                </div>
                                <div class="half-padded meals-row border-bottom">
                                    <div class="padded stretch">
                                        <ptc-ui-icon name="icon.object.meal-lunch"></ptc-ui-icon> Mittagessen
                                    </div>
                                    <div class="right icon input">
                                        <input
                                            name="mealValueLunch"
                                            type="number"
                                            step="0.01"
                                            class="text-right-aligning"
                                        />
                                        <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>
                                    </div>
                                </div>
                                <div class="half-padded meals-row">
                                    <div class="padded stretch">
                                        <ptc-ui-icon name="icon.object.meal-dinner"></ptc-ui-icon> Abendessen
                                    </div>
                                    <div class="right icon input">
                                        <input
                                            name="mealValueDinner"
                                            type="number"
                                            step="0.01"
                                            class="text-right-aligning"
                                        />
                                        <ptc-ui-icon name="icon.object.currency"></ptc-ui-icon>
                                    </div>
                                </div>
                            </div>

                            <div
                                class="padded evenly stretching spacing horizontal layout"
                                ?hidden=${!this._hasMealsSettingsChanged}
                            >
                                <button class="primary">Speichern</button>
                                <button class="transparent" type="button" @click=${() => this._reset()}>
                                    Abbrechen
                                </button>
                            </div>
                        </form>

                        <h2>Weitere Sonderleistungen</h2>
                        <div class="box">
                            <ptc-sortable-list
                                .items=${this._benefitTypes}
                                .renderItem=${(type: BenefitType, i: number) => html`
                                    <div class="border-bottom relative">
                                        <div class="double-padded click" @click=${() => this._editBenefitType(type)}>
                                            ${type.name}
                                        </div>

                                        <button
                                            id="benefitTypeMenuButton${i}"
                                            class="transparent slim margined top right absolute"
                                            type="button"
                                        >
                                            <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                                        </button>

                                        <ptc-ui-popover
                                            for="benefitTypeMenuButton${i}"
                                            placement="bottom"
                                            class="popover-menu"
                                            data-popover="close"
                                        >
                                            <button @click=${() => this._editBenefitType(type)} type="button">
                                                <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                Bearbeiten
                                            </button>

                                            <button @click=${() => this._removeBenefitType(i)} type="button">
                                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                Entfernen
                                            </button>
                                        </ptc-ui-popover>
                                    </div>
                                `}
                                @item-moved=${this._orderChanged}
                            ></ptc-sortable-list>

                            <div class="margined vertical layout" @click=${() => this._addBenefitType()}>
                                <button class="transparent">
                                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neue Sonderleistung
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ptc-scroller>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
